import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ASSETS, BOOK_URL } from "@/lib/site";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// For the free static-hosting setup: set REACT_APP_FORMSPREE_ENDPOINT to your
// Formspree form URL (e.g. https://formspree.io/f/xxxxxxx). When present, the
// form submits there (no backend needed). Otherwise it uses the built-in API.
const FORMSPREE = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    plan: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      if (FORMSPREE) {
        await axios.post(FORMSPREE, form, {
          headers: { Accept: "application/json" },
        });
      } else {
        await axios.post(`${API}/leads`, form);
      }
      toast.success("Request received — we'll be in touch to book your free assessment.");
      setForm({ name: "", email: "", phone: "", suburb: "", plan: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try the booking link instead.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/15 py-3 text-white placeholder:text-white/30 focus:border-[#169ED9] focus:outline-none transition-colors duration-300";

  return (
    <section
      id="book"
      data-testid="lead-section"
      className="relative bg-[#050505] py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#169ED9]">
              / Book your free assessment
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Protect Your Home. Starting Today.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-white/55 leading-relaxed max-w-md">
              Leave your details and our team will reach out to arrange your free onsite
              inspection and quotation. Prefer to book instantly?
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href={BOOK_URL}
              data-testid="lead-book-online-btn"
              className="mt-6 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.15em] text-[#169ED9] hover:text-white transition-colors duration-300"
            >
              Book online now
              <span>→</span>
            </a>
          </Reveal>
          <Reveal delay={0.2}>
            <img
              src={ASSETS.tpecoWithLicense}
              alt="The Plumbing & Electrical Co. — licensed"
              data-testid="lead-tpeco-logo"
              className="mt-14 h-36 w-36 object-contain opacity-95"
            />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="lead-name"
                className={field}
                placeholder="Full name *"
                value={form.name}
                onChange={update("name")}
              />
              <input
                data-testid="lead-phone"
                className={field}
                placeholder="Phone *"
                value={form.phone}
                onChange={update("phone")}
              />
            </div>
            <input
              data-testid="lead-email"
              type="email"
              className={field}
              placeholder="Email *"
              value={form.email}
              onChange={update("email")}
            />
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="lead-suburb"
                className={field}
                placeholder="Suburb"
                value={form.suburb}
                onChange={update("suburb")}
              />
              <select
                data-testid="lead-plan"
                className={`${field} appearance-none cursor-pointer [&>option]:bg-[#0a0a0a]`}
                value={form.plan}
                onChange={update("plan")}
              >
                <option value="">Preferred plan</option>
                <option value="No Money Down">No Money Down — $55/mo</option>
                <option value="Monthly Installment">Monthly Installment — $70/mo</option>
                <option value="Buy Outright">Buy Outright — $2,640</option>
              </select>
            </div>
            <textarea
              data-testid="lead-message"
              rows={3}
              className={`${field} resize-none`}
              placeholder="Tell us about your property (optional)"
              value={form.message}
              onChange={update("message")}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit-btn"
              className="w-full rounded-full bg-white text-black py-4 text-sm font-semibold tracking-wide hover:bg-[#169ED9] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Request Free Assessment"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
